import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Stack,
  Chip,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
  Avatar,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Search,
  Refresh,
  Close,
  FilterList,
  DirectionsBike,
  SwapHoriz,
  LocalShipping,
  ShoppingBag,
  ClearAll,
  Assignment,
  FiberManualRecord,
  Phone,
  LocationOn,
} from "@mui/icons-material";
import api, {
  fetchRidersAdmin,
  assignRiderToOrder,
  updateOrderStatus,
} from "../../api/api";
import { useSnackbar } from "../../utils/snackbar";

/* ---------------------------------------------------------------
   CONSTANTS & HELPERS
--------------------------------------------------------------- */
const KES = (n) =>
  `KES ${Number(n || 0).toLocaleString("en-KE", { minimumFractionDigits: 2 })}`;

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const STATUS_META = {
  created:   { label: "Created",   color: "default"  },
  paid:      { label: "Paid",      color: "info"     },
  shipped:   { label: "In Transit", color: "primary" },
  completed: { label: "Delivered",  color: "success" },
  cancelled: { label: "Cancelled",  color: "error"   },
};

const ALL_STATUSES = Object.keys(STATUS_META);

const statusChip = (status) => {
  const m = STATUS_META[status] || { label: status, color: "default" };
  return <Chip label={m.label} color={m.color} size="small" sx={{ fontWeight: 600 }} />;
};

/* ---------------------------------------------------------------
   STAT CARD
--------------------------------------------------------------- */
function StatCard({ label, value, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        textAlign: "center",
      }}
    >
      <Typography variant="h4" fontWeight={800} color={color || "text.primary"}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
        {label}
      </Typography>
    </Paper>
  );
}

/* ---------------------------------------------------------------
   ASSIGN RIDER DIALOG
--------------------------------------------------------------- */
function AssignRiderDialog({ open, order, riders, onClose, onAssigned }) {
  const { showAlert } = useSnackbar();
  const [riderId, setRiderId] = useState(order?.rider?._id || "");
  const [loading, setLoading] = useState(false);

  // sync when order changes
  useEffect(() => {
    setRiderId(order?.rider?._id || "");
  }, [order]);

  const handleAssign = async () => {
    if (!riderId) return;
    try {
      setLoading(true);
      await assignRiderToOrder(order._id, riderId);
      showAlert("Rider assigned successfully.", "success");
      onAssigned();
      onClose();
    } catch (err) {
      showAlert(err.response?.data?.message || "Failed to assign rider.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <DirectionsBike color="primary" />
          {order?.rider ? "Reassign Rider" : "Assign Rider"}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {order && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Order #{order._id?.slice(-6).toUpperCase()} — {order.user?.name || "Customer"}
            </Typography>
            {order.rider && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Currently assigned to: <strong>{order.rider.name}</strong>
              </Alert>
            )}
          </Box>
        )}
        <FormControl fullWidth size="small" disabled={loading}>
          <InputLabel>Select Rider</InputLabel>
          <Select
            label="Select Rider"
            value={riderId}
            onChange={(e) => setRiderId(e.target.value)}
          >
            {riders
              .filter((r) => r.active !== false)
              .map((r) => (
                <MenuItem key={r._id} value={r._id}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: r.isOnline ? "success.main" : "text.disabled",
                      }}
                    />
                    {r.name} — {r.phone}
                  </Stack>
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={loading || !riderId}
          startIcon={loading ? <CircularProgress size={16} /> : <Assignment />}
        >
          {loading ? "Assigning…" : "Assign Rider"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------------------------------------------------------
   UPDATE STATUS DIALOG
--------------------------------------------------------------- */
function UpdateStatusDialog({ open, order, onClose, onUpdated }) {
  const { showAlert } = useSnackbar();
  const [status, setStatus] = useState(order?.status || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setStatus(order?.status || "");
  }, [order]);

  const handleUpdate = async () => {
    if (!status || status === order?.status) return;
    try {
      setLoading(true);
      await updateOrderStatus(order._id, status);
      showAlert("Order status updated.", "success");
      onUpdated();
      onClose();
    } catch (err) {
      showAlert(err.response?.data?.message || "Failed to update status.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Update Order Status</DialogTitle>
      <DialogContent dividers>
        {order && (
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Order #{order._id?.slice(-6).toUpperCase()}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1} mt={1}>
              <Typography variant="body2">Current:</Typography>
              {statusChip(order.status)}
            </Stack>
          </Box>
        )}
        <FormControl fullWidth size="small" disabled={loading}>
          <InputLabel>New Status</InputLabel>
          <Select
            label="New Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {ALL_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {STATUS_META[s]?.label || s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={loading || !status || status === order?.status}
          startIcon={loading ? <CircularProgress size={16} /> : <LocalShipping />}
        >
          {loading ? "Updating…" : "Update Status"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------------------------------------------------------
   MAIN COMPONENT
--------------------------------------------------------------- */
export default function ManageRiderOrders() {
  const { showAlert } = useSnackbar();

  /* ---- data ---- */
  const [riders, setRiders]   = useState([]);
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  /* ---- filters ---- */
  const [riderFilter, setRiderFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch]             = useState("");
  const [dateFrom, setDateFrom]         = useState("");
  const [dateTo, setDateTo]             = useState("");

  /* ---- dialogs ---- */
  const [assignDialog, setAssignDialog]       = useState(false);
  const [assignTarget, setAssignTarget]       = useState(null);

  const [statusDialog, setStatusDialog]       = useState(false);
  const [statusTarget, setStatusTarget]       = useState(null);

  /* ---- order detail drawer ---- */
  const [drawerOpen, setDrawerOpen]   = useState(false);
  const [drawerOrder, setDrawerOrder] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  /* ---- load ---- */
  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchRidersAdmin();
      setRiders(res.data?.riders || []);
      setOrders(res.data?.orders || []);
    } catch (err) {
      setError("Failed to load data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  /* ---- clear filters ---- */
  const clearFilters = () => {
    setRiderFilter("all");
    setStatusFilter("all");
    setSearch("");
    setDateFrom("");
    setDateTo("");
  };

  const hasFilters =
    riderFilter !== "all" ||
    statusFilter !== "all" ||
    search.trim() !== "" ||
    dateFrom !== "" ||
    dateTo !== "";

  /* ---- derived stats ---- */
  const stats = useMemo(() => {
    const total     = orders.length;
    const inTransit = orders.filter((o) => o.status === "shipped").length;
    const delivered = orders.filter((o) => o.status === "completed").length;
    const unassigned = orders.filter((o) => !o.rider).length;
    return { total, inTransit, delivered, unassigned };
  }, [orders]);

  /* ---- filtered orders ---- */
  const filtered = useMemo(() => {
    let list = [...orders];

    if (riderFilter !== "all") {
      list = riderFilter === "unassigned"
        ? list.filter((o) => !o.rider)
        : list.filter((o) => String(o.rider?._id) === String(riderFilter));
    }

    if (statusFilter !== "all") {
      list = list.filter((o) => o.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((o) =>
        `${o._id} ${o.user?.name} ${o.user?.phone} ${o.rider?.name}`.toLowerCase().includes(q)
      );
    }

    if (dateFrom) {
      list = list.filter((o) => new Date(o.createdAt) >= new Date(dateFrom));
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((o) => new Date(o.createdAt) <= to);
    }

    // newest first
    return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [orders, riderFilter, statusFilter, search, dateFrom, dateTo]);

  /* ---- open order detail drawer ---- */
  const openOrderDrawer = async (order) => {
    setDrawerOpen(true);
    setDrawerOrder(order);
    setDrawerLoading(true);
    try {
      const res = await api.get(`/api/orders/${order._id}`);
      setDrawerOrder(res.data);
    } catch (err) {
      // fall back to list-level data already in state
    } finally {
      setDrawerLoading(false);
    }
  };

  /* ---- open assign dialog ---- */
  const openAssign = (order) => {
    setAssignTarget(order);
    setAssignDialog(true);
  };

  /* ---- open status dialog ---- */
  const openStatus = (order) => {
    setStatusTarget(order);
    setStatusDialog(true);
  };

  /* ===========================================================
     RENDER
  =========================================================== */
  return (
    <Box sx={{ p: 3, bgcolor: "background.default", minHeight: "100vh" }}>

      {/* PAGE HEADER */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="text.primary">
            Rider Order Management
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.3}>
            Assign riders, track deliveries, and update order status
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={load} disabled={loading} sx={{ border: "1px solid", borderColor: "divider" }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* STATS ROW */}
      <Stack direction={{ xs: "column", sm: "row" }} gap={2} mb={3}>
        <StatCard label="Total Orders" value={stats.total} color="primary" />
        <StatCard label="In Transit" value={stats.inTransit} color="info.main" />
        <StatCard label="Delivered" value={stats.delivered} color="success.main" />
        <StatCard label="Unassigned" value={stats.unassigned} color="error.main" />
      </Stack>

      {/* ERROR */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* FILTER BAR */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <FilterList fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={700}>Filters</Typography>
          {hasFilters && (
            <Button
              size="small"
              variant="text"
              startIcon={<ClearAll />}
              onClick={clearFilters}
              sx={{ ml: "auto", color: "text.secondary" }}
            >
              Clear All
            </Button>
          )}
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} gap={2} flexWrap="wrap">
          {/* Search */}
          <TextField
            placeholder="Search order ID, customer, rider…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Rider filter */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Rider</InputLabel>
            <Select
              label="Filter by Rider"
              value={riderFilter}
              onChange={(e) => setRiderFilter(e.target.value)}
            >
              <MenuItem value="all">All Riders</MenuItem>
              <MenuItem value="unassigned">
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "error.main" }} />
                  Unassigned
                </Stack>
              </MenuItem>
              {riders.map((r) => (
                <MenuItem key={r._id} value={r._id}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: r.isOnline ? "success.main" : "text.disabled",
                      }}
                    />
                    {r.name} — {r.phone}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {ALL_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_META[s]?.label || s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Date range */}
          <TextField
            label="From"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 150 }}
          />
        </Stack>

        <Typography variant="caption" color="text.secondary" mt={1.5} display="block">
          Showing {filtered.length} of {orders.length} order{orders.length !== 1 ? "s" : ""}
        </Typography>
      </Paper>

      {/* ORDERS TABLE */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "background.default" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Order</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Rider Assigned</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Delivery Address</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8, color: "text.secondary" }}>
                      <LocalShipping sx={{ fontSize: 40, mb: 1, opacity: 0.3, display: "block", mx: "auto" }} />
                      No orders found matching your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((o) => {
                    const addr = o.deliveryAddress;
                    const addrStr = typeof addr === "string"
                      ? addr
                      : addr
                      ? `${addr.street || ""} ${addr.area || ""}`.trim()
                      : "—";

                    return (
                      <TableRow key={o._id} hover>
                        {/* Order ID */}
                        <TableCell>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.light" }}>
                              <ShoppingBag sx={{ fontSize: 16 }} />
                            </Avatar>
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ cursor: "pointer", color: "primary.main", "&:hover": { textDecoration: "underline" } }}
                                onClick={() => openOrderDrawer(o)}
                              >
                                #{o._id.slice(-6).toUpperCase()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {o.items?.length || 0} item{o.items?.length !== 1 ? "s" : ""}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Customer */}
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {o.user?.name || "N/A"}
                          </Typography>
                          {o.user?.phone && (
                            <Stack direction="row" alignItems="center" gap={0.5}>
                              <Phone sx={{ fontSize: 12, color: "text.secondary" }} />
                              <Typography variant="caption" color="text.secondary">
                                {o.user.phone}
                              </Typography>
                            </Stack>
                          )}
                        </TableCell>

                        {/* Rider */}
                        <TableCell>
                          {o.rider ? (
                            <Stack direction="row" alignItems="center" gap={1}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  bgcolor: riders.find((r) => r._id === o.rider._id)?.isOnline
                                    ? "success.main"
                                    : "text.disabled",
                                  flexShrink: 0,
                                }}
                              />
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {o.rider.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {o.rider.phone}
                                </Typography>
                              </Box>
                            </Stack>
                          ) : (
                            <Chip
                              label="Unassigned"
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </TableCell>

                        {/* Status */}
                        <TableCell>{statusChip(o.status)}</TableCell>

                        {/* Delivery Address */}
                        <TableCell>
                          {addrStr !== "—" ? (
                            <Stack direction="row" alignItems="flex-start" gap={0.5}>
                              <LocationOn sx={{ fontSize: 14, color: "text.secondary", mt: 0.2, flexShrink: 0 }} />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ maxWidth: 160, wordBreak: "break-word" }}
                              >
                                {addrStr}
                              </Typography>
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>

                        {/* Total */}
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}>
                            {KES(o.total)}
                          </Typography>
                        </TableCell>

                        {/* Date */}
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {fmtDate(o.createdAt)}
                          </Typography>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Stack direction="row" gap={0.5} justifyContent="flex-end">
                            <Tooltip title={o.rider ? "Reassign Rider" : "Assign Rider"}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openAssign(o)}
                              >
                                <DirectionsBike fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Update Status">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => openStatus(o)}
                                disabled={o.status === "completed" || o.status === "cancelled"}
                              >
                                <SwapHoriz fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ============================================================
          ASSIGN RIDER DIALOG
      ============================================================ */}
      <AssignRiderDialog
        open={assignDialog}
        order={assignTarget}
        riders={riders}
        onClose={() => { setAssignDialog(false); setAssignTarget(null); }}
        onAssigned={load}
      />

      {/* ============================================================
          UPDATE STATUS DIALOG
      ============================================================ */}
      <UpdateStatusDialog
        open={statusDialog}
        order={statusTarget}
        onClose={() => { setStatusDialog(false); setStatusTarget(null); }}
        onUpdated={load}
      />

      {/* ============================================================
          ORDER DETAIL DRAWER
      ============================================================ */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 460 },
            bgcolor: "background.default",
          },
        }}
      >
        {/* Drawer header */}
        <Box
          sx={{
            p: 3,
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={800}>Order Details</Typography>
              {drawerOrder && (
                <Typography variant="caption" color="text.secondary">
                  #{drawerOrder._id?.slice(-6).toUpperCase()}
                </Typography>
              )}
            </Box>
            <IconButton size="small" onClick={() => setDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ p: 3, overflowY: "auto", flex: 1 }}>
          {drawerLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : drawerOrder ? (
            <Stack gap={3}>
              {/* Status + actions */}
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                {statusChip(drawerOrder.status)}
                <Stack direction="row" gap={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DirectionsBike />}
                    onClick={() => { setDrawerOpen(false); openAssign(drawerOrder); }}
                  >
                    {drawerOrder.rider ? "Reassign" : "Assign Rider"}
                  </Button>
                  {drawerOrder.status !== "completed" && drawerOrder.status !== "cancelled" && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SwapHoriz />}
                      onClick={() => { setDrawerOpen(false); openStatus(drawerOrder); }}
                    >
                      Status
                    </Button>
                  )}
                </Stack>
              </Stack>

              {/* Customer */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Customer
                </Typography>
                <Typography fontWeight={700} mt={0.5}>{drawerOrder.user?.name || "—"}</Typography>
                <Typography variant="body2" color="text.secondary">{drawerOrder.user?.phone || "—"}</Typography>
                {drawerOrder.user?.email && (
                  <Typography variant="body2" color="text.secondary">{drawerOrder.user.email}</Typography>
                )}
              </Paper>

              {/* Rider */}
              <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Assigned Rider
                </Typography>
                {drawerOrder.rider ? (
                  <Stack direction="row" alignItems="center" gap={1.5} mt={0.5}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main" }}>
                      <DirectionsBike sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box>
                      <Typography fontWeight={700}>{drawerOrder.rider.name}</Typography>
                      <Typography variant="body2" color="text.secondary">{drawerOrder.rider.phone}</Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Chip label="Not assigned" size="small" color="error" variant="outlined" sx={{ mt: 0.5 }} />
                )}
              </Paper>

              {/* Delivery address */}
              {drawerOrder.deliveryAddress && (
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                    Delivery Address
                  </Typography>
                  <Typography variant="body2" mt={0.5}>
                    {typeof drawerOrder.deliveryAddress === "string"
                      ? drawerOrder.deliveryAddress
                      : `${drawerOrder.deliveryAddress.street || ""}, ${drawerOrder.deliveryAddress.area || ""}`.replace(/^,\s*|,\s*$/, "")}
                  </Typography>
                </Paper>
              )}

              {/* Items */}
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  Items ({drawerOrder.items?.length || 0})
                </Typography>
                <Stack gap={1}>
                  {(drawerOrder.items || []).map((it, idx) => (
                    <Stack
                      key={idx}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Typography variant="body2">
                        {it.product?.title || it.title || "Item"} × {it.quantity}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {KES(it.priceAtPurchase * it.quantity)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              <Divider />

              {/* Total */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={700}>Total</Typography>
                <Typography fontWeight={800} variant="h6" color="primary.main">
                  {KES(drawerOrder.total)}
                </Typography>
              </Stack>

              <Typography variant="caption" color="text.secondary">
                Placed: {fmt(drawerOrder.createdAt)}
              </Typography>
            </Stack>
          ) : null}
        </Box>
      </Drawer>
    </Box>
  );
}
